/**
 * PURPOSE: Selector "Firmas previas | Firmas finales" de la barra de la Vista Planilla FEDUP (FTR-0062), a la izquierda de "Actualizar".
 * RESPONSIBILITIES:
 *  - Sí: control segmentado con ícono de pluma y un contador discreto por modo (2/4, 3/5); por defecto ninguno activo; pulsar un modo
 *    lo activa y pulsarlo otra vez lo desactiva (aria-pressed).
 *  - Sí: por debajo de `xl` (1140 px, el breakpoint de withMT) se reduce a un ícono con un menú de dos opciones, sin empujar los demás
 *    controles (se renderiza UNA de las dos variantes según el ancho de la ventana).
 *  - No: no muestra avisos ni bloquea nada; con el partido oficializado sigue visible (el candado aparece al pulsar una casilla).
 * COLLABORATORS:
 *  - FedupPreviewModal.jsx, useFedupSignatureController.js, fedupSignatures.utils.js
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/FedupSignatureModeSelector.jsx
 */

import React, { useEffect, useRef, useState } from "react";
import { FaPenNib } from "react-icons/fa6";
import { FiCheck } from "react-icons/fi";

import { SIGNATURE_MODE_LIST, modeCounter } from "./fedupSignatures.utils.js";

/** Mismo umbral que `xl:` de Tailwind en winscore-web (withMT: 1140 px). */
export const SIGNATURE_SELECTOR_WIDE_QUERY = "(min-width: 1140px)";

function useMediaQuery(query, fallback = true) {
  const get = () => {
    try {
      return typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : fallback;
    } catch {
      return fallback;
    }
  };
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
}

const counterLabel = (status, mode) => {
  const info = (status?.modes || []).find((m) => m.mode === mode);
  return info ? `${info.signed} de ${info.total} firmadas` : "estado de firmas no disponible";
};

export default function FedupSignatureModeSelector({ status, mode, onToggleMode, disabled = false, official = false }) {
  const wide = useMediaQuery(SIGNATURE_SELECTOR_WIDE_QUERY);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);
  const lockHint = official ? " · partido oficializado" : "";

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [menuOpen]);

  useEffect(() => {
    if (wide) setMenuOpen(false);
  }, [wide]);

  if (wide) {
    return (
      <div
        role="group"
        aria-label="Firmas"
        data-fedup-signature-selector="full"
        className="inline-flex h-9 shrink-0 items-stretch overflow-hidden rounded-lg border border-slate-300 bg-white text-slate-700 shadow-sm"
      >
        <span className="inline-flex items-center pl-2.5 pr-1 text-sm text-slate-500" aria-hidden="true">
          <FaPenNib />
        </span>
        {SIGNATURE_MODE_LIST.map(({ mode: m, label, title }) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              onClick={() => onToggleMode(m)}
              title={`${title}${lockHint}`}
              className={[
                "inline-flex items-center gap-1.5 border-l border-slate-200 px-2.5 text-[11px] font-bold transition first-of-type:border-l-0",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40",
                active ? "bg-emerald-50 text-emerald-800" : "hover:bg-slate-50",
              ].join(" ")}
            >
              <span>{label}</span>
              <span className={["text-[10px] font-semibold tabular-nums", active ? "text-emerald-600" : "text-slate-400"].join(" ")} aria-label={counterLabel(status, m)}>
                {modeCounter(status, m)}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative shrink-0" data-fedup-signature-selector="compact">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Firmas"
        title={`Firmas${lockHint}`}
        disabled={disabled}
        onClick={() => setMenuOpen((v) => !v)}
        className={[
          "inline-flex h-9 w-9 items-center justify-center rounded-lg border text-sm shadow-sm transition",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40",
          mode ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
        ].join(" ")}
      >
        <FaPenNib aria-hidden="true" />
      </button>
      {menuOpen ? (
        <div
          role="menu"
          aria-label="Firmas"
          className="absolute right-0 top-full z-30 mt-1 w-56 rounded-lg border border-slate-200 bg-white p-1 text-slate-700 shadow-lg"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              // Esc cierra el menú, no el visor.
              event.stopPropagation();
              setMenuOpen(false);
            }
          }}
        >
          {SIGNATURE_MODE_LIST.map(({ mode: m, label, title }) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                role="menuitemcheckbox"
                aria-checked={active}
                title={`${title}${lockHint}`}
                onClick={() => {
                  setMenuOpen(false);
                  onToggleMode(m);
                }}
                className={[
                  "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-xs font-bold",
                  active ? "bg-emerald-50 text-emerald-800" : "hover:bg-slate-50",
                ].join(" ")}
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-flex w-3 justify-center text-emerald-600" aria-hidden="true">
                    {active ? <FiCheck /> : null}
                  </span>
                  {label}
                </span>
                <span className="text-[10px] font-semibold tabular-nums text-slate-400" aria-label={counterLabel(status, m)}>
                  {modeCounter(status, m)}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
